import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  Container,
  VStack,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Grid,
  Image,
  Text,
  HStack,
  Flex,
  Spinner,
  Center
} from '@chakra-ui/react';
import '../styles/Rankings.css';
import { rankingService } from '../services/rankingService';

interface Match {
  id: string;
  team_a: string;
  team_b: string;
  score_a: number | null;
  score_b: number | null;
  phase: string;
  activo: boolean;
  team_a_logo: string;
  team_b_logo: string;
}

interface Prediction {
  id: string;
  user_id: string;
  match_id: string;
  predicted_score_a: number;
  predicted_score_b: number;
}

interface User {
  id: string;
  uuid: string;
  name: string;
  email: string;
  predictions: Prediction[];
}

interface Ranking {
  name: string;
  points: number;
}

const VALID_USERS = [
  { email: 'htorres@teiker.mx', name: 'HectorT' },
  { email: 'ralvarez@teiker.mx', name: 'Ramiro' },
  { email: 'ohernandez@teiker.mx', name: 'Oscar 🛌' },
  { email: 'jhernandez@teiker.mx', name: 'Chuy 🧑🏿‍🦱' },
  { email: 'erodriguez@teiker.mx', name: 'Neto' },
  { email: 'gmartinez@teiker.mx', name: 'Gus' },
  { email: 'lgama@teiker.mx', name: 'Gama 🧙🏻‍♂️' },
  { email: 'e.lugo@teiker.mx', name: 'Lugo 🎱' },
  { email: 'mrangel@teiker.mx', name: 'Alex' },
  { email: 'frojo@teiker.mx', name: 'Fer 🧂' },
  { email: 'lcardona@teiker.mx', name: 'Cardona' },
  { email: 'egonzalez@teiker.mx', name: 'Edgar' },
  { email: 'juanjo@juanjo.mx', name: 'JuanJo 👶🏼' },
  { email: 'alicia@teiker.mx', name: 'Alicia 🪦' },
  { email: 'cflores@teiker.mx', name: 'Charly' },
  { email: 'jmontemayor@teiker.mx', name: 'Jeannesy' },
  { email: 'palcala@teiker.mx', name: 'Paty' },
  { email: 'vtorres@teiker.mx', name: 'Vianey' },
  { email: 'calvarez@teiker.mx', name: 'Ivon' },
  { email: 'jherrera@teiker.mx', name: 'Pepe 💅🏻' }
];

const getUserAvatar = (name: string): string | undefined => {
  switch (name) {
    case 'Ramiro':
      return '/images/ramiro.png';
    case 'Gus':
      return '/images/el_gus.png';
    case 'Neto':
      return '/images/neto2.png';
    default:
      return undefined;
  }
};

const getPhaseLabel = (phase: string) => {
  switch (phase) {
    case 'Quarters':
      return 'Cuartos de Final';
    case 'Semifinals':
      return 'Semifinales';
    case 'Final':
      return 'Final';
    default:
      return `Jornada ${phase}`;
  }
};

const removeDuplicatePredictions = async (predictions: Prediction[]) => {
  const seen = new Set<string>();
  const toDelete: string[] = [];

  predictions.forEach(pred => {
    const key = `${pred.user_id}_${pred.match_id}`;
    if (seen.has(key)) {
      toDelete.push(pred.id);
    } else {
      seen.add(key);
    }
  });

  for (const id of toDelete) {
    try {
      await deleteDoc(doc(db, "predictions", id));
      console.log(`Eliminada predicción duplicada: ${id}`);
    } catch (e) {
      console.error(`Error eliminando predicción duplicada ${id}:`, e);
    }
  }
};

const Rankings = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [userPoints, setUserPoints] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [rankings, setRankings] = useState<Ranking[]>([]);
  const [overallRankings, setOverallRankings] = useState<Ranking[]>([]);
  const [currentPhase, setCurrentPhase] = useState<string>('');
  const [matches, setMatches] = useState<{[key: string]: Match[]}>({});
  const [teamLogos, setTeamLogos] = useState<{ [teamName: string]: string }>({});

  const calculatePoints = (prediction: Prediction, match: Match): number => {
    if (match.score_a === null || match.score_b === null) return 0;

    let points = 0;

    // 1. Marcador exacto (3 puntos)
    if (prediction.predicted_score_a === match.score_a && 
        prediction.predicted_score_b === match.score_b) {
      points = 3;
      console.log('Marcador exacto: 3 puntos');
      return points; // Si acierta el marcador exacto, no necesitamos revisar más
    }

    // 2. Empate (1 punto)
    if (match.score_a === match.score_b && 
        prediction.predicted_score_a === prediction.predicted_score_b) {
      points = 1;
      console.log('Empate acertado: 1 punto');
      return points;
    }

    // 3. Ganador correcto (1 punto)
    const matchResult = Math.sign(match.score_a - match.score_b);
    const predictionResult = Math.sign(prediction.predicted_score_a - prediction.predicted_score_b);
    
    if (matchResult === predictionResult && matchResult !== 0) {
      points = 1;
      console.log('Ganador correcto: 1 punto');
    }

    return points;
  };

  const calculatePointsForUser = (
    userPreds: Prediction[],
    matchesById: { [key: string]: Match }
  ) => {
    let points = 0;

    // Suma puntos normales para todas las predicciones
    userPreds.forEach(pred => {
      const match = matchesById[pred.match_id];
      if (match) {
        points += calculatePoints(pred, match);
      }
    });

    // Suma puntos extra por etapa si corresponde
    const etapas = ['cuartos', 'semifinal', 'final'];
    etapas.forEach(etapa => {
      // Encuentra todos los partidos de la etapa en matchesById
      const partidosEtapa = Object.values(matchesById).filter(m =>
        m.phase && m.phase.toLowerCase().includes(etapa)
      );

      // Deben existir exactamente 2 partidos (ida y vuelta) para la etapa
      if (partidosEtapa.length !== 2) return;

      // Solo si TODOS los partidos tienen resultado real
      const todosJugados = partidosEtapa.every(m => m.score_a !== null && m.score_b !== null);

      // Predicciones del usuario para la etapa
      const predsEtapa = userPreds.filter(p => {
        const match = matchesById[p.match_id];
        return match && match.phase && match.phase.toLowerCase().includes(etapa);
      });

      // Solo si el usuario tiene predicción para TODOS los partidos de la etapa
      if (todosJugados && predsEtapa.length === 2) {
        // Suma goles reales y pronosticados
        const sumaGolesReales = partidosEtapa.reduce(
          (acc, m) => acc + (m.score_a ?? 0) + (m.score_b ?? 0), 0
        );
        const sumaGolesPronosticados = predsEtapa.reduce(
          (acc, p) => acc + (p.predicted_score_a ?? 0) + (p.predicted_score_b ?? 0), 0
        );

        if (sumaGolesReales === sumaGolesPronosticados && sumaGolesReales > 0) {
          points += 2;
        }
      }
    });

    return points;
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      // 1. Cargar todos los usuarios
      const usersSnap = await getDocs(collection(db, 'users'));
      const usersData = usersSnap.docs.map(doc => ({
        id: doc.id,
        uuid: doc.data().uuid,
        ...doc.data()
      })) as User[];
      setUsers(usersData);

      // 2. Cargar todas las predicciones
      const predictionsSnap = await getDocs(collection(db, 'predictions'));
      const predictions = predictionsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Prediction[];

      // Elimina duplicados en Firestore
      await removeDuplicatePredictions(predictions);

      // Filtrar duplicados: solo la primera predicción por usuario y partido
      const uniquePredictionsMap = new Map<string, Prediction>();
      predictions.forEach(pred => {
        const key = `${pred.user_id}_${pred.match_id}`;
        if (!uniquePredictionsMap.has(key)) {
          uniquePredictionsMap.set(key, pred);
        }
      });
      const uniquePredictions = Array.from(uniquePredictionsMap.values());

      // 3. Cargar todos los partidos y agrupar por fase
      const matchesRef = collection(db, 'matches');
      const matchesSnapshot = await getDocs(matchesRef);
      const matchesById: { [key: string]: Match } = {};
      const matchesByPhase: { [key: string]: Match[] } = {};

      matchesSnapshot.forEach(doc => {
        const data = doc.data();
        const match: Match = {
          id: String(doc.id),
          team_a: data.team_a,
          team_b: data.team_b,
          score_a: data.score_a !== null ? Number(data.score_a) : null,
          score_b: data.score_b !== null ? Number(data.score_b) : null,
          phase: data.phase,
          activo: Boolean(data.activo),
          team_a_logo: data.team_a_logo,
          team_b_logo: data.team_b_logo
        };
        matchesById[match.id] = match;
        if (!matchesByPhase[match.phase]) matchesByPhase[match.phase] = [];
        matchesByPhase[match.phase].push(match);
      });

      // 4. Calcular puntos por usuario
      const points: Record<string, number> = {};
      usersData.forEach(user => {
        const userPreds = uniquePredictions.filter(p => p.user_id === user.uuid);
        points[user.uuid] = calculatePointsForUser(userPreds, matchesById);
      });
      setUserPoints(points);

      // Rankings
      const rankings = VALID_USERS.map(validUser => {
        const user = usersData.find(u => u.email === validUser.email);
        const userPointsValue = user ? points[user.uuid] || 0 : 0;
        return {
          name: validUser.name,
          points: userPointsValue
        };
      });

      setRankings(rankings.sort((a, b) => b.points - a.points));
      setOverallRankings(rankings.sort((a, b) => b.points - a.points));
      setMatches(matchesByPhase);

      Object.values(matchesById).forEach(match => {
        if (match.score_a !== null && match.score_b !== null) {
          console.log(`Partido real: ${match.id} (${match.team_a} vs ${match.team_b}) Resultado: ${match.score_a}-${match.score_b}`);
        }
      });

      uniquePredictions.forEach(pred => {
        console.log(`Predicción: usuario ${pred.user_id}, partido ${pred.match_id}, scores: ${pred.predicted_score_a}-${pred.predicted_score_b}`);
      });

      setLoading(false);
    };

    loadData();
  }, []);

  useEffect(() => {
    const fetchLogos = async () => {
      const logos: { [teamName: string]: string } = {};
      for (const match of Object.values(matches).flat()) {
        if (match.team_a && !logos[match.team_a]) {
          logos[match.team_a] = (await rankingService.getTeamLogo(match.team_a)) || '';
        }
        if (match.team_b && !logos[match.team_b]) {
          logos[match.team_b] = (await rankingService.getTeamLogo(match.team_b)) || '';
        }
      }
      setTeamLogos(logos);
    };
    if (Object.keys(matches).length > 0) {
      fetchLogos();
    }
  }, [matches]);

  if (loading) {
    return (
      <Center p={8}>
        <Spinner size="xl" />
      </Center>
    );
  }

  const activePhases = Object.entries(matches).filter(
    ([phase, phaseMatches]) => phase && phaseMatches.some(m => m.activo)
  );

  return (
    <Container maxW="container.xl" py={5}>
      <VStack spacing={6} align="stretch">
        <Heading textAlign="center">Tabla de Posiciones</Heading>

        {/* Rankings por fase */}
        {currentPhase && (
          <>
            <Heading size="lg" textAlign="center">Ranking - {getPhaseLabel(currentPhase)}</Heading>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Posición</Th>
                  <Th>Usuario</Th>
                  <Th>Puntos</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rankings.map((user, index) => (
                  <Tr key={index}>
                    <Td>{index + 1}</Td>
                    <Td>
                      <HStack>
                        <Text>{user.name}</Text>
                        {getUserAvatar(user.name) && (
                          <Image 
                            src={getUserAvatar(user.name)} 
                            alt={user.name}
                            boxSize={user.name === 'Gus' ? "35px" : "30px"}
                          />
                        )}
                      </HStack>
                    </Td>
                    <Td>{user.points}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </>
        )}

        {/* Rankings totales */}
        <Heading size="lg" textAlign="center">Puntos Acumulados</Heading>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th></Th>
              <Th>Posición</Th>
              <Th>Usuario</Th>
              <Th>Puntos Totales</Th>
            </Tr>
          </Thead>
          <Tbody>
            {overallRankings.map((user, index) => (
              <Tr key={index}>
                <Td width="10px">
                  <Box
                    height="4px"
                    bg={
                      index < 4 ? "blue.500" :
                      index < 7 ? "yellow.500" :
                      index < 9 ? "green.500" :
                      index >= overallRankings.length - 2 ? "red.500" : "transparent"
                    }
                    borderRadius="2px"
                  />
                </Td>
                <Td>{index + 1}</Td>
                <Td>
                  <HStack>
                    <Text>{user.name}</Text>
                    {getUserAvatar(user.name) && (
                      <Image 
                        src={getUserAvatar(user.name)} 
                        alt={user.name}
                        boxSize={user.name === 'Gus' ? "35px" : "30px"}
                      />
                    )}
                  </HStack>
                </Td>
                <Td>{user.points}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {/* Mostrar partidos por fase */}
        {activePhases.map(([phase, phaseMatches]) => (
          <Box key={phase}>
            <Heading size="lg" textAlign="center" mt={8}>
              {getPhaseLabel(phase)}
            </Heading>
            <Grid templateColumns="repeat(2, 1fr)" gap={6} mt={4}>
              {phaseMatches.map(match => (
                <Box key={match.id} p={4} borderWidth={1} borderRadius="lg">
                  <Flex justify="space-between" align="center">
                    <HStack flex="1">
                      <Image
                        src={teamLogos?.[match.team_a || ''] || '/team-placeholder.png'}
                        alt={match.team_a}
                        boxSize="32px"
                        objectFit="contain"
                      />
                      <Text>{match.team_a}</Text>
                    </HStack>
                    <Box textAlign="center" mx={4}>
                      <Text fontWeight="bold">{match.score_a ?? '-'}</Text>
                      <Text color="gray.500">VS</Text>
                      <Text fontWeight="bold">{match.score_b ?? '-'}</Text>
                    </Box>
                    <HStack flex="1" justify="flex-end">
                      <Text>{match.team_b}</Text>
                      <Image
                        src={teamLogos?.[match.team_b || ''] || '/team-placeholder.png'}
                        alt={match.team_b}
                        boxSize="32px"
                        objectFit="contain"
                      />
                    </HStack>
                  </Flex>
                  <Text mt={2} color="gray.500" textAlign="center">
                    {!match.score_a && !match.score_b 
                      ? 'Pronósticos aún habilitados'
                      : 'Pronósticos cerrados'}
                  </Text>
                </Box>
              ))}
            </Grid>
          </Box>
        ))}
      </VStack>
    </Container>
  );
};

export default Rankings; 